/**
 * La lista dei messaggi: bolle, divisori di pagina, puntini di attesa, errore
 * con «Riprova», domande suggerite.
 *
 * `aria-live` sta solo sul messaggio finale: annunciare ogni frammento in
 * arrivo farebbe rileggere allo screen reader la stessa frase dieci volte.
 */

import { useEffect, useRef, useState } from "react";
import { IconaRiprova } from "./icone";
import { Markdown } from "./Markdown";
import { prossimaFrase } from "./frasi-attesa";
import type { Voce } from "./storia";

interface Proprieta {
  voci: Voce[];
  inArrivo: string | null;
  occupato: boolean;
  errore: string | null;
  suggerimenti: string[];
  onSuggerimento: (testo: string) => void;
  onRiprova?: () => void;
  onRicomincia?: () => void;
  /** Il nodo su cui sta l'operatore: i link verso di esso restano testo. */
  idNodoCorrente: string | null;
}

const SOGLIA_ANCORAGGIO = 80;

const CAMBIO_FRASE_MS = 2200;

/** Rotellina e frase che cambia, finche' non arriva il primo token. */
function Attesa() {
  const [frase, setFrase] = useState(() => prossimaFrase(null, Math.random));
  useEffect(() => {
    const t = window.setInterval(() => setFrase((f) => prossimaFrase(f, Math.random)), CAMBIO_FRASE_MS);
    return () => window.clearInterval(t);
  }, []);
  return (
    <div className="assistente__pensa" role="status">
      <span className="assistente__rotella" aria-hidden="true" />
      <span className="assistente__pensa-frase">{frase}</span>
    </div>
  );
}

export function Messaggi({
  voci,
  inArrivo,
  occupato,
  errore,
  suggerimenti,
  onSuggerimento,
  onRiprova,
  onRicomincia,
  idNodoCorrente,
}: Proprieta) {
  const contenitore = useRef<HTMLDivElement>(null);
  const ancorato = useRef(true);

  // Resta in fondo mentre arriva testo, ma solo se l'operatore non e' risalito
  // a rileggere: in quel caso lo scorrimento automatico sarebbe un fastidio.
  useEffect(() => {
    const el = contenitore.current;
    if (!el || !ancorato.current) return;
    el.scrollTop = el.scrollHeight;
  }, [voci, inArrivo, occupato, errore]);

  const suScorrimento = () => {
    const el = contenitore.current;
    if (!el) return;
    ancorato.current = el.scrollHeight - el.scrollTop - el.clientHeight < SOGLIA_ANCORAGGIO;
  };

  const ultimaAssistente = [...voci].reverse().find((v) => v.tipo === "assistente");

  return (
    <div className="assistente__messaggi" ref={contenitore} onScroll={suScorrimento}>
      {voci.map((voce, i) => {
        if (voce.tipo === "pagina") {
          return (
            <div key={i} className="assistente__divisore" role="separator">
              Ora sei su <strong>{voce.titolo}</strong>
            </div>
          );
        }
        if (voce.tipo === "ricominciata") {
          return (
            <div key={i} className="assistente__divisore" role="separator">
              Conversazione ricominciata
            </div>
          );
        }
        if (voce.tipo === "utente") {
          return (
            <div key={i} className="assistente__bolla assistente__bolla--utente">
              {voce.testo}
            </div>
          );
        }
        const finale = voce === ultimaAssistente && !inArrivo;
        return (
          <div
            key={i}
            className="assistente__bolla assistente__bolla--assistente"
            aria-live={finale ? "polite" : undefined}
          >
            <Markdown testo={voce.testo} idNodoCorrente={idNodoCorrente} />
            {voce.troncata ? <p className="assistente__troncata">Risposta interrotta: non è completa.</p> : null}
          </div>
        );
      })}

      {inArrivo !== null ? (
        <div className="assistente__bolla assistente__bolla--assistente">
          <Markdown testo={inArrivo} idNodoCorrente={idNodoCorrente} />
        </div>
      ) : null}

      {occupato && inArrivo === null ? <Attesa /> : null}

      {errore ? (
        <div className="assistente__errore" role="alert">
          <span>{errore}</span>
          {onRiprova ? (
            <button type="button" className="assistente__riprova" onClick={onRiprova}>
              <IconaRiprova dimensione={16} /> Riprova
            </button>
          ) : null}
          {onRicomincia ? (
            <button type="button" className="assistente__riprova" onClick={onRicomincia}>
              <IconaRiprova dimensione={16} /> Ricomincia e rifai la domanda
            </button>
          ) : null}
        </div>
      ) : null}

      {!occupato && !errore && suggerimenti.length > 0 ? (
        <div className="assistente__suggerimenti">
          {suggerimenti.map((s) => (
            <button key={s} type="button" className="assistente__chip" onClick={() => onSuggerimento(s)}>
              {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
